<template>
  <o-modal :active="isActive" :canCancel="false" class="modal-sm">
    <div class="modal-card">
      <!-- Modal Header -->
      <header class="modal-card-head">
        <h4 class="modal-card-title">
          {{editMode ? `Edit Template Order: ${card.templateName}` : 'Create Template Order'}}
          <span
            class="line-height required-label is-size-5 is-italic is-pulled-right mr-0 has-text-weight-normal"
          >
            * required
          </span>
        </h4>
      </header>
      <!-- Modal Body -->
      <div class="modal-card-body is-visible px-0 pt-0">
        <div class="column">
          <div class="columns">
            <div class="column py-0">
              <h4 class="is-size-3 is-italic has-text-weight-semibold">
                Template Details
              </h4>
            </div>
            <div class="column py-0">
              <h4 class="is-size-3 is-italic has-text-weight-semibold">
                Order Details
              </h4>  
            </div>
          </div>
          <div class="columns">
            <div class="column pt-0">
              <div class="field" v-if="!isMaterial && !editMode">
                <h3 class="has-text-black-bis is-italic is-size-3 has-text-weight-bold line-height">
                  Template Type *
                </h3>
                <div class="field-body">
                  <mf-multi-select
                    v-model="templateType"
                    placeholder="Select Option"
                    :preselect-first="true"
                    :options="templateTypeOpts"
                    :searchable="true"
                    :allow-empty="false"
                    label="name"
                    track-by="value"
                    @update:modelValue="updatePurpose"
                  />
                </div>
              </div>
							<div class="field">
                <h3 class="has-text-black-bis is-italic is-size-3 has-text-weight-bold line-height">
                  Template Name *
                </h3>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="card.templateName"
                      placeholder="Enter name (3 or more characters)"
                    />
                  </div>
                </div>
              </div>
							<div class="field">
                <h3 class="has-text-black-bis is-italic is-size-3 has-text-weight-bold line-height">
                  Template ID *
                </h3>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="card.templateId"
                      placeholder="4 - 16 Characters"
                    />
                  </div>
                </div>
              </div>
              <div class="field string-select" v-if="!isMaterial">
                <mf-multi-select
                  v-if="card.purpose !== 'cutOrder' && !card.isDefault"
                  v-model="card.stageUsed"
                  :options="stages"
                  :multiple="false"
                  titleLabel="Stage Used"
                  labelClass="has-text-black-bis is-italic is-size-3"
                  :allow-empty="false"
                ></mf-multi-select>
                <template v-else>
                  <h3 class="has-text-black-bis is-italic is-size-3 has-text-weight-bold line-height">
                    Stage Used
                  </h3>
                  <div class="field-body">
                    <h3 class="has-text-black-bis is-italic is-size-3 line-height">
                      {{ $_.capitalize(card.stageUsed) }}
                    </h3>
                  </div>
                </template>
              </div>
            </div>
            <div class="column pt-0">
              <div class="field">
                <h3 class="has-text-black-bis is-italic is-size-3 has-text-weight-bold line-height">
                  Name *
                </h3>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      v-model.trim="card.name"
                      placeholder="Enter name (3 or more characters)"
                    />
                  </div>
                </div>
              </div>
							<div class="empty-height" v-if="!card.owner?.user"></div>
							<field-user-select v-else
                  titleLabel="Owner"
                  :isEditing="true"
                  :key="allCompanyUsers.length"
                  :options="allCompanyUsers"
                  :value="card.owner.user"
                  @update:value="(val) => $_.set(card, 'owner.user', val)"
                ></field-user-select>
							<div class="field" v-if="!isMaterial && !editMode">
                <h3 class="has-text-black-bis is-italic is-size-3 has-text-weight-bold line-height">
                  Work Steps *
                </h3>
                <div class="field-body">
                  <mf-multi-select
                    v-model="defaultRun"
                    placeholder="Select Option"
                    :preselect-first="true"
                    :options="companyRuns"
                    :searchable="true"
                    :allow-empty="false"
                    label="name"
                    track-by="_id"
                  />
                </div>
              </div>
              <div class="field" v-if="card.purpose !== 'cutOrder' && editMode">
                <h3 class="has-text-black-bis is-italic is-size-3 has-text-weight-bold line-height">
                  Default Template
                </h3>
                <div class="field-body">
                  <div class="control">
                    <o-checkbox v-model="card.isDefault" class="mx-0 p-0" @update:model-value="updateStageUsed"
                    v-tooltip="card.items?.length ? 'Default templates cannot have items!' : ''"
                    :disabled="card.items?.length">
                    </o-checkbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Modal Footer -->
      <footer
        class="modal-card-foot is-justify-content-flex-end is-align-items-center"
      >
        <div class="column is-narrow">
          <div>
            <button class="button is-outlined" @click="$emit('close')">
              Cancel
            </button>
            <button
              class="button has-background-black-bis"
              @click="save"
              :disabled="disableSave"
            >
              {{ editMode ? 'Update Template' : 'Create / View Template'}}
            </button>
          </div>
        </div>
      </footer>
      <o-loading
        :full-page="true"
        :active="isLoading"
        :can-cancel="true"
      ></o-loading>
    </div>
  </o-modal>
</template>
<script>
import { reactive, toRefs, computed, onBeforeMount } from "vue";
import { capitalize, cloneDeep, filter, isEmpty, set, toString } from "lodash";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import Projects from "@/models/Projects";
import QtyInput from "@/components/fields/QtyInput.vue";
import MfFilePicker from "@/components/MfFilePicker.vue";
import { BaseOrder } from '@/models/BaseOrder';
import Users from "@/models/Users";
import CardEditMixin from "@/components/mixins/CardEditMixin";

export default {
  name: "CreateEditTemplateOrder",
  emits: ["close", "create"],
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    projectId: {
			type: String,
			default: '',
			required: true,
		},
    isMaterial: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    selectedTemplate: {
      default: () => {},
      type: Object
    }
  },
  components: {
    QtyInput,
    "mf-file-picker": MfFilePicker,
  },
  setup(props, { emit }) {
    const store = useStore();
    const toast = useToast();
    const router = useRouter();

    const { saveCard } = CardEditMixin();

    const templateTypeOpts = [
      {
        name: "Production Order Template",
        value: "prodTemplates",
      },
      {
        name: "Cut Order Template",
        value: "cutOrder",
      },
    ];

    const stages = ["All", "Coordination", "Detailing", "Manufacturing"];
    const data = reactive({
      templateType: {
        name: "Production Order Template",
        value: "prodTemplates",
      },
			card: {},
			allCompanyUsers: [],
			isLoading: false,
			companyRuns: [],
			defaultRun: {},
      templateProject: {}
    });

		const getAllCompanyUsers = async () => {
    const user = store.state.userData;
      let allUsers;
      const orderCreator = {
        _id: "(Order Creator)",
        name: "(Order Creator)",
        companyName: user.companyName,
      };
			allUsers = (await Users.allUsers({ limit: 1000 })).data;
			allUsers = filter(allUsers, (user) => user.activated && !user.archived);
			allUsers.unshift(orderCreator);

      data.allCompanyUsers = Users.groupUsers(allUsers);
    };

    onBeforeMount(async () => {
			try {
				data.isLoading = true
        let projectId = props.projectId
        if (isEmpty(projectId)) {
          const projectObj = {
            name: store.state.userData.company,
            isTemplateProject: true,
            location: {},
            createdCompany: store.state.userData.company,
            status: 'Active',
            projectLocations: [],
          };
          const templateProject = await Projects.update(projectObj);
          projectId = templateProject._id
        }
        data.templateProject = await Projects.getOne(projectId);
				await store.getters.userPromise;
        if (props.editMode) {
          data.card = cloneDeep(props.selectedTemplate)
          data.card.stageUsed = capitalize(data.card.stageUsed)
        } else {
          await store.dispatch('getCompanyRuns');
          data.companyRuns = store.state.companyRuns;
          data.companyRuns.forEach((r) => { r.copiedFrom = r.copiedFrom || r._id; });
          const user = store.state.userData;
          const obj = {
            stage: props.isMaterial ? 'preparation' : 'prodTemplates',
            isManager: props.isMaterial ? false : true,
            __t: props.isMaterial ? 'MatTemplates' : 'ProdTemplates',
            _customStage: props.isMaterial ? 'matTemplates' : 'prodTemplates',
            project: data.templateProject,
            owner: {
              user: { _id: user._id, name: user.name },
              company: { _id: user.company, name: user.companyName },
            },
          }
          if (!props.isMaterial) {
            obj.manager = {
              owner: {
                user: { _id: user._id, name: user.name },
                company: { _id: user.company, name: user.companyName },
              }
            }
          }
          data.card = new BaseOrder(obj);
          data.card.stageUsed = 'All'
        }
				await getAllCompanyUsers()
			} catch (e) {
				console.log('Error: ', e)
			} finally {
				data.isLoading = false
			}
		});

    const disableSave = computed(() => {
      if (props.isMaterial || props.editMode) {
        return isEmpty(data.card.templateName) || isEmpty(data.card.templateId) || isEmpty(data.card.name)
      }
      if (props.editMode) {

      }
      return isEmpty(data.card.templateName) || isEmpty(data.card.templateId) || isEmpty(data.card.name) || isEmpty(data.defaultRun)
    })

    const templateCardValidations = (card) => {
      let nameError = false;
      let runError = false;
      if (!card.templateName) {
        nameError = true;
      }
      if (isEmpty(data.defaultRun) && !props.isMaterial && !props.editMode) runError = true;
      if (nameError && runError) {
        return 'Template Name and Work Step are required fields';
      }
      if (nameError) {
        return 'Template Name is a required field.';
      }
      if (runError) {
        return 'Work Step is a required field.';
      }
      if (card.templateName.length < 3) {
        return 'Template Name should be minimum of three characters';
      }
      if (isEmpty(toString(card.templateId)) || card.templateId.length < 4 || card.templateId.length > 16) {
        return 'Template Id should be have a Minimum of 4 Characters and Maximum of 16 Characters';
      }
      if (data.card.purpose === 'cutOrder') {
        const dateToSet = (data.card.leadDates.detailing === null || data.card.leadDates.detailing === '') ? null : 0;
        set(data.card.leadDates, 'coordination', dateToSet);
        set(data.card.leadDates, 'manufacturing', dateToSet);
        set(data.card.leadDates, 'qa', dateToSet);
        if (data.card.leadDates.detailing === '') set(data.card.leadDates, 'detailing', dateToSet);
      }
      return false;
    }


    const save = async () => {
      try {
        data.isLoading = true
        const validationResult = templateCardValidations(data.card);
        if (validationResult) {
          toast.error(validationResult);
          return false;
        }
        // add run
        data.card.stageUsed = data.card.stageUsed ? data.card.stageUsed.toLowerCase() : "all";
        if (data.card.purpose === 'cutOrder') {
          data.card.stageUsed = 'detailing';
        }
        if (!props.isMaterial && !props.editMode) {
          const run = data.card.manager.addRun();
          if (run.stats && !run.stats.hasOwnProperty('percComplete')) {
            run.stats.percComplete = 0;
          }
          ({ name: run.name, copiedFrom: run.copiedFromId } = data.defaultRun)
          data.card.manager.runItemsIndexMap();
        }
        if (props.editMode) {
          const cardToSave = new BaseOrder(data.card)
          await cardToSave.save()
          emit('close', {refresh: true})
        } else {
          const newCard = await saveCard('add', "", cloneDeep(data.card));
          // route to card view.
          router.push({ name: `${store.state.activeScreen === 'prodTemplates' ? 'production' : 'material'}-template-edit`, params: {
            projectId: data.templateProject._id,
            cardId: newCard._id,
          }})
        }

      } catch (e) {
        console.error('Error: ', e)
        toast.error(e.data?.msg || 'Error creating template order', )
      } finally {
        data.isLoading = false
      }
    }

    const updatePurpose = () => {
      data.card.purpose = data.templateType.value === 'prodTemplates' ?  'general' : 'cutOrder'
      data.card.stageUsed = 'detailing'
      // stage used.
    }

    const updateStageUsed = (val) => {
      if (data.card.stageUsed !== 'All' && val) {
        toast.warning('Stage Used will be reset to All')
        data.card.stageUsed = 'All'
      }
    }

    return {
      ...toRefs(data),
      templateTypeOpts,
      save,
      disableSave,
      updatePurpose,
      stages,
      updateStageUsed
    };
  },
};
</script>
<style scoped>
	.modal-card-head {
		background-color: #fff !important;
		border-bottom: none !important;
	}

	.modal-card-foot {
		border-top: 1px solid #84888c;
		background-color: #fff !important;
	}

	::v-deep(.o-field.has-text-centered.drag-drop) {
		text-align: start !important;
	}

	::v-deep(.o-upl) {
		height: auto !important;
		display: block !important;
	}

	.empty-box {
		height: 200px !important;
	}
</style>